import { useCallback, useState } from 'react';
import type { StudioRewriteText } from '@okrapdf/pdf-studio-plugin/studio';
import { getClerkToken } from '@/api/clerkTokenBridge';
import { useStudioRewriteMutation, useStudioRouteMutation } from '@/api/toolsApi';
import { getBrand } from '@/lib/brand';
import {
  trackStudioPromptFailed,
  trackStudioPromptRouted,
  trackStudioPromptSubmitted,
  trackStudioRewriteRun,
} from '@/lib/posthog';

/**
 * Browser side of Okra Agent (W.4.105): turn a sentence into the Studio tool
 * that does it, and rewrite one Edit text block.
 *
 * The Agent (`POST /api/ai/studio`) answers when the visitor is signed in on a
 * deploy that has it (`studioAgentEnabled`). The local keyword matcher answers
 * otherwise and on every failure — a rate or daily limit included — so a
 * prompt always gets a reply. Only the prompt, the tool catalogue, the file name and
 * the page count leave the browser — and, for a rewrite, the one text block.
 * Never the file.
 */

/**
 * Whether this deploy has the Agent. Only the default brand's Worker has the
 * model binding; on the other brand every call would answer 503. The one place
 * that decides it — the Agent tool, its top-bar button, the rewrite seam and
 * the network half of routing all ask here.
 */
export function studioAgentEnabled(): boolean {
  return getBrand().id === 'okrapdf';
}

export interface IntentTool {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  requiresDocument: boolean;
}

export interface IntentDocument {
  name: string;
  pageCount: number;
}

/** One request of a prompt that asks for several things, and what does it (W.4.132). */
export interface IntentPart {
  /** The request in the visitor's words. */
  request: string;
  /** The tool that does it, or null when no tool here does. */
  toolId: string | null;
  /**
   * No tool does it, but it asks what the PDF says (a summary, an
   * explanation), which Okra Agent answers once it has the PDF. `route` keeps
   * it only for a prompt sent with no PDF, on a deploy that has the Agent.
   */
  agent: boolean;
}

export interface IntentResult {
  toolId: string | null;
  reply: string;
  alternatives: string[];
  engine: 'agent' | 'local';
  /**
   * Set when the prompt asks for more than one thing: each request and what
   * does it. An Agent answer that leaves them out gets only the requests no
   * tool here does, from the local matcher.
   */
  parts?: IntentPart[];
}

export interface RouteIntentInput {
  prompt: string;
  tools: IntentTool[];
  document: IntentDocument | null;
  activeToolId: string | null;
}

// ── Local matcher ────────────────────────────────────────────────────────────

/** A prompt word found in a tool's title or keywords. */
const STRONG_HIT = 3;
/** A prompt word found only in a tool's description, or a generic word wherever it is found. */
const WEAK_HIT = 1;
/** An everyday phrase from `EVERYDAY_PHRASES`. */
const PHRASE_HIT = 4;
/** The tool's own name is in the prompt. Beats a phrase, so "word to pdf" is not "pdf to word". */
const NAME_HIT = 6;
/** One title or keyword hit is enough to open a tool; description words alone need three. */
const MATCH_THRESHOLD = 3;
const MAX_ALTERNATIVES = 3;
/** An alternative must score at least this share of the winner's score. */
const ALTERNATIVE_FLOOR = 0.5;

const NO_MATCH_REPLY = 'I could not match that to a tool. Pick one below.';

/** Fold a plural onto its singular, so "typos" finds `typo` and "photos" finds `photo`. */
function stem(word: string): string {
  return word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word;
}

function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .map(stem)
    .join(' ');
}

/**
 * Words that say nothing about which tool is meant. "pdf", "to" and "page" are
 * here because half the catalogue has them in a title or keyword; they still
 * count inside a phrase or a typed-out tool name.
 */
const STOP_WORDS = new Set(
  (
    'a all also an and any are as at be but by can could do document each every file for from ' +
    'get has have help here how i in into is it its just like make me more my need new not of ' +
    'on or other our out page pdf please should so some that the them then there these this ' +
    'those to up want we with would you your'
  )
    .split(' ')
    .map(stem),
);

/**
 * Everyday wording → tool id, so plain requests work offline. Phrases match
 * whole words only; a tool the host did not pass is never scored, so an id
 * here cannot leak into a result.
 */
const EVERYDAY_PHRASES = new Map(
  Object.entries({
    redact: ['hide', 'black out', 'censor', 'remove personal', 'ssn', 'phone'],
    'edit-text': ['typo', 'wording', 'change the text', 'rewrite', 'fix the date', 'edit'],
    'pdf-to-link': ['link', 'url', 'send', 'share', 'publish'],
    merge: ['combine', 'join', 'merge'],
    split: ['split', 'extract pages', 'separate'],
    organize: ['reorder', 'rearrange', 'delete page', 'move pages'],
    sign: ['sign', 'signature'],
    compress: ['smaller', 'shrink', 'reduce size', 'compress'],
    rotate: ['rotate', 'sideways', 'upside down'],
    ocr: ['searchable', 'scan', 'ocr'],
    'pdf-to-word': ['word', 'docx'],
    'pdf-to-excel': ['excel', 'xlsx', 'spreadsheet'],
    watermark: ['watermark', 'stamp'],
    'page-numbers': ['page numbers', 'number the pages'],
    'fill-pdf-form': ['fill', 'form'],
    crop: ['crop', 'trim margins'],
    highlight: ['highlight'],
    'add-image-to-pdf': ['image', 'logo', 'picture'],
    compare: ['compare', 'diff'],
  }).map(([id, phrases]): [string, string[]] => [id, phrases.map(normalise)]),
);

/**
 * Title and keyword words that several unrelated tools share, or that mean
 * something else in everyday requests ("phone numbers" is not Page numbers).
 * One of these alone does not pick a tool and does not make it an alternative.
 */
const GENERIC_WORDS = new Set(['numbers', 'add', 'remove', 'convert', 'export'].map(stem));

/** Words that ask for something on the page to go. */
const REMOVAL_WORDS = new Set(['remove', 'delete', 'erase', 'strip', 'rid'].map(stem));

/**
 * Tools that put something on a page and cannot take it off. A prompt that
 * asks for a removal is never theirs: Page numbers adds numbers, it does not
 * delete the ones a PDF already has.
 */
const ADDS_ONLY = new Set([
  'page-numbers',
  'bates',
  'watermark',
  'sign',
  'add-text-to-pdf',
  'add-image-to-pdf',
]);

/**
 * Text drawn on the page that a removal prompt may name → the tool that takes
 * it off. Edit text deletes a text block ("Remove this text"). Remove
 * annotations only takes off what lies over the page, and page numbers,
 * headers and footers are usually page content.
 */
const REMOVAL_PHRASES = new Map([
  ['edit-text', ['page numbers', 'header', 'footer'].map(normalise)],
]);

/** Words of a request about what the PDF says. No tool does that; Okra Agent answers it from the PDF. */
const READING_WORDS = new Set(
  [
    'summarize',
    'summarise',
    'summary',
    'explain',
    'what',
    'who',
    'why',
    'tell',
    'mean',
    'read',
  ].map(stem),
);

/**
 * Where one request may end and the next begin: a comma, a sentence end,
 * "and", "then". Captured, so a piece that turns out to be part of the
 * request before it is put back together as it was typed.
 */
const REQUEST_SEPARATOR =
  /(\s*(?:[,;&]|[.!?](?=\s)|\b(?:and then|and also|and|then|also|plus)\b)\s*)/i;
/** A separator that only joins ("and", "&", a comma), as in "find and replace the date". */
const JOINING_SEPARATOR = /^(?:\s*(?:[,&]|\band\b)\s*)+$/i;

/**
 * The words a request of its own starts with: a verb that asks for something,
 * or the start of a question. A piece that starts with anything else is more
 * of the request before it: "remove the header and footer", "add page numbers
 * and dates". A word that is often a noun in these prompts ("stamp",
 * "watermark", "date", "email", "black") is left out on purpose.
 */
const REQUEST_WORDS = new Set(
  (
    'add insert put place make turn convert change edit fix correct replace find search rewrite ' +
    'reword retype update translate summarize summarise explain describe tell show give count ' +
    'extract pull get take remove delete erase strip clear clean hide redact censor blur mask ' +
    'anonymize anonymise sign fill complete highlight underline annotate crop trim rotate flip ' +
    'resize scale shrink compress reduce optimize optimise split separate divide merge combine join ' +
    'append organize organise reorder rearrange sort move swap duplicate ocr scan flatten repair ' +
    'protect encrypt lock unlock secure share send publish upload download save export print keep ' +
    'open close compare check verify validate review proofread read analyze analyse create generate ' +
    'write prepare rename number what who whom whose why how when where which is are does do did ' +
    'can could would will should i we let need want help'
  ).split(' '),
);

/** Words in front of a request that say nothing about it. */
const FILLER_WORDS = new Set(['please', 'pls', 'kindly', 'just', 'now']);

/**
 * A request every tool ends with, or that needs none: "rotate and save" is one
 * request, to rotate. Dropped from the list only when no tool matches it, so
 * "save as Word" is still PDF to Word.
 */
const TRIVIAL_WORDS = new Set(['save', 'download', 'print', 'open', 'close']);

const MAX_PARTS = 5;
const MAX_PART_CHARS = 120;

function meaningfulWords(text: string): string[] {
  return normalise(text)
    .split(' ')
    .filter((word) => word !== '' && !STOP_WORDS.has(word));
}

/** Whole-word containment over normalised text. */
function hasPhrase(text: string, phrase: string): boolean {
  return phrase !== '' && ` ${text} `.includes(` ${phrase} `);
}

interface ToolScore {
  score: number;
  /** Any title or keyword word of the tool is in the prompt, generic or not. */
  listed: boolean;
  /** The prompt points at this tool: a telling title or keyword word, its name, or an everyday phrase. */
  telling: boolean;
}

function scoreTool(
  tool: IntentTool,
  text: string,
  words: Set<string>,
  removing: boolean,
): ToolScore {
  const titleWords = meaningfulWords(tool.title);
  const strong = new Set(titleWords);
  const keywordPhrases: string[] = [];
  for (const keyword of tool.keywords) {
    const normalised = normalise(keyword);
    if (normalised.includes(' ')) keywordPhrases.push(normalised);
    else if (normalised) strong.add(normalised);
  }
  const weak = new Set(meaningfulWords(tool.description));

  let score = 0;
  let listed = false;
  let telling = false;
  for (const word of words) {
    if (strong.has(word)) {
      listed = true;
      if (GENERIC_WORDS.has(word)) {
        score += WEAK_HIT;
      } else {
        score += STRONG_HIT;
        telling = true;
      }
    } else if (weak.has(word)) {
      score += WEAK_HIT;
    }
  }
  for (const phrase of keywordPhrases) {
    if (!hasPhrase(text, phrase)) continue;
    score += STRONG_HIT;
    listed = true;
    telling = true;
  }
  // The name as it reads (title, or id: "pdf to word"), or every telling word
  // of a longer title in any order ("extract the images"). Order only counts
  // for the typed-out form, which is what keeps the two conversions apart.
  const named =
    hasPhrase(text, normalise(tool.title)) ||
    hasPhrase(text, normalise(tool.id)) ||
    (titleWords.length > 1 && titleWords.every((word) => words.has(word)));
  if (named) {
    score += NAME_HIT;
    listed = true;
    telling = true;
  }
  const phrases = [
    ...(EVERYDAY_PHRASES.get(tool.id) ?? []),
    ...(removing ? (REMOVAL_PHRASES.get(tool.id) ?? []) : []),
  ];
  for (const phrase of phrases) {
    if (!hasPhrase(text, phrase)) continue;
    score += PHRASE_HIT;
    listed = true;
    telling = true;
  }
  return { score, listed, telling };
}

/** One request against the host's tools. A removal is never matched to a tool that only adds. */
function matchRequest(prompt: string, tools: IntentTool[]): IntentResult {
  const text = normalise(prompt);
  const words = new Set(meaningfulWords(prompt));
  const removing = [...words].some((word) => REMOVAL_WORDS.has(word));
  const ranked = tools
    .map((tool, index) => ({ tool, index, ...scoreTool(tool, text, words, removing) }))
    .filter((entry) => entry.score > 0 && !(removing && ADDS_ONLY.has(entry.tool.id)))
    .sort((a, b) => b.score - a.score || a.index - b.index);

  const best = ranked[0];
  if (!best || best.score < MATCH_THRESHOLD) {
    return {
      toolId: null,
      reply: NO_MATCH_REPLY,
      alternatives: ranked
        .filter((entry) => entry.listed)
        .slice(0, MAX_ALTERNATIVES)
        .map((entry) => entry.tool.id),
      engine: 'local',
    };
  }
  return {
    toolId: best.tool.id,
    // Nothing has opened when this is read: the reply offers the tool.
    reply: `${best.tool.title} fits this.`,
    alternatives: ranked
      .slice(1)
      .filter((entry) => entry.telling && entry.score >= best.score * ALTERNATIVE_FLOOR)
      .slice(0, MAX_ALTERNATIVES)
      .map((entry) => entry.tool.id),
    engine: 'local',
  };
}

/** The words of a piece as typed, lower-cased, with the filler in front of it taken off. */
function leadingWords(piece: string): string[] {
  const words = piece.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  let start = 0;
  while (start < words.length - 1 && FILLER_WORDS.has(words[start])) start += 1;
  return words.slice(start);
}

/** One word, filler aside: "find", "rotate". */
function isLoneWord(piece: string): boolean {
  return leadingWords(piece).length === 1;
}

/** The piece starts a request of its own: a verb that asks for something, or a question. */
function startsRequest(piece: string): boolean {
  const first = leadingWords(piece)[0];
  return first !== undefined && REQUEST_WORDS.has(first);
}

/** The piece asks for something by itself: a tool does it, or it asks what the PDF says. */
function doesSomething(piece: string, tools: IntentTool[]): boolean {
  return matchRequest(piece, tools).toolId !== null || asksWhatItSays(piece);
}

/**
 * The requests of a prompt, in order. A piece is a request of its own only
 * when it starts like one; anything else stays with the request before it, so
 * "remove the header and footer" is one request, except in a list of lone
 * words ("rotate, crop, watermark"), where each that a tool does is one. A
 * lone verb no tool does, joined by "and" to the next request, shares its
 * object: "find and replace the date" is one request too. A piece with no
 * meaningful word ("it", "too") and one that every tool does anyway ("save")
 * are not listed.
 */
function splitRequests(prompt: string, tools: IntentTool[]): string[] {
  const tokens = prompt.split(REQUEST_SEPARATOR);
  const pieces: { text: string; joiner: string }[] = [];
  let joiner = '';
  tokens.forEach((token, index) => {
    // Odd tokens are the captured separators.
    if (index % 2 === 1) {
      joiner += token;
      return;
    }
    const text = token.trim();
    if (!text) return;
    const previous = pieces[pieces.length - 1];
    // In a list of lone words ("rotate, crop, watermark") a tool's word is a request too.
    const listed =
      previous !== undefined &&
      isLoneWord(text) &&
      isLoneWord(previous.text) &&
      matchRequest(text, tools).toolId !== null;
    if (previous && !startsRequest(text) && !listed) previous.text += `${joiner}${text}`;
    else pieces.push({ text, joiner });
    joiner = '';
  });

  for (let index = 0; index < pieces.length - 1; index += 1) {
    const piece = pieces[index];
    const next = pieces[index + 1];
    if (
      isLoneWord(piece.text) &&
      JOINING_SEPARATOR.test(next.joiner) &&
      !doesSomething(piece.text, tools)
    ) {
      next.text = `${piece.text}${next.joiner}${next.text}`;
      next.joiner = piece.joiner;
      pieces.splice(index, 1);
      index -= 1;
    }
  }

  return pieces
    .map((piece) => piece.text)
    .filter((piece) => meaningfulWords(piece).length > 0)
    .filter(
      (piece) => !TRIVIAL_WORDS.has(leadingWords(piece)[0] ?? '') || doesSomething(piece, tools),
    )
    .slice(0, MAX_PARTS);
}

/** Each request of the prompt and what does it, or [] when the prompt asks for one thing. */
function localParts(prompt: string, tools: IntentTool[]): IntentPart[] {
  const requests = splitRequests(prompt, tools);
  if (requests.length < 2) return [];
  return requests.map((request) => {
    const { toolId } = matchRequest(request, tools);
    return {
      request: request.slice(0, MAX_PART_CHARS),
      toolId,
      agent: toolId === null && asksWhatItSays(request),
    };
  });
}

/** A request about what the PDF says, which Okra Agent answers from the file. */
function asksWhatItSays(request: string): boolean {
  return meaningfulWords(request).some((word) => READING_WORDS.has(word));
}

/**
 * Deterministic keyword match of a prompt against the host's tools. Ties keep
 * the host's tool order. Pure: no network, no clock.
 *
 * Alternatives are tools the prompt could also mean, not the runners-up of the
 * ranking: beside a winner, one needs a telling hit of its own and at least
 * half the winner's score; with no winner, a title or keyword word in the
 * prompt. A tool that only shares a description word is never offered.
 *
 * A prompt that asks for several things is matched one request at a time: the
 * tool is the first request's that has one, the other requests' tools are the
 * alternatives, and `parts` says what does each, a request no tool does too.
 */
export function matchIntentLocally(prompt: string, tools: IntentTool[]): IntentResult {
  const whole = matchRequest(prompt, tools);
  const parts = localParts(prompt, tools);
  // Nothing here does any of them, or one tool does all of them: one answer covers it.
  if (!parts.some((part) => part.toolId !== null || part.agent)) return whole;
  const toolIds = [...new Set(parts.flatMap((part) => (part.toolId ? [part.toolId] : [])))];
  if (toolIds.length === 1 && parts.every((part) => part.toolId === toolIds[0])) return whole;

  const toolId = toolIds[0] ?? null;
  const main = tools.find((tool) => tool.id === toolId);
  return {
    toolId,
    // Said here: the tool covers one request, and the list under the reply says what does the rest.
    reply: main ? `${main.title} fits part of this.` : NO_MATCH_REPLY,
    alternatives: [...new Set([...toolIds.slice(1), ...whole.alternatives])]
      .filter((id) => id !== toolId)
      .slice(0, MAX_ALTERNATIVES),
    engine: 'local',
    parts,
  };
}

/**
 * Which request of `parts` the Agent's tool is for: the one the local matcher
 * gave it too, else the one its own words fit best. -1 when that is not
 * known: no request fits it, or two fit it as well.
 */
function placeTool(tool: IntentTool, parts: IntentPart[]): number {
  const matched = parts.findIndex((part) => part.toolId === tool.id);
  if (matched !== -1) return matched;
  let best = -1;
  let bestScore = 0;
  let tied = false;
  parts.forEach((part, index) => {
    const words = new Set(meaningfulWords(part.request));
    const removing = [...words].some((word) => REMOVAL_WORDS.has(word));
    const { score } = scoreTool(tool, normalise(part.request), words, removing);
    if (score > bestScore) {
      best = index;
      bestScore = score;
      tied = false;
    } else if (score > 0 && score === bestScore) {
      tied = true;
    }
  });
  return tied ? -1 : best;
}

/**
 * The requests of a multi-part prompt that no tool here does, for an Agent
 * answer that did not list its parts. Such an answer picked one tool for the
 * whole prompt; the request it is for is not named here, and every other
 * request the local matcher has no tool for is. When where the Agent's tool
 * goes is not known, only a request that asks what the PDF says, which no
 * tool does, is named: any other might be the one the Agent's tool covers.
 */
function uncoveredParts(result: IntentResult, prompt: string, tools: IntentTool[]): IntentPart[] {
  const parts = localParts(prompt, tools);
  if (result.toolId === null) return parts.filter((part) => part.toolId === null);
  const tool = tools.find((candidate) => candidate.id === result.toolId);
  const placed = tool ? placeTool(tool, parts) : -1;
  if (placed === -1) return parts.filter((part) => part.agent);
  return parts.filter((part, index) => index !== placed && part.toolId === null);
}

// ── No PDF yet ───────────────────────────────────────────────────────────────

/** A sentence that says the work is done or about to be: never true before a PDF is there. */
const PROMISE =
  /\b(?:i['’]ll|i will|i['’]m going to|i am going to|i['’]ve|i have|let me|done)\b|\bi (?:removed|added|changed|deleted|made|did|summari[sz]ed)\b/i;

/**
 * The answer as the visitor reads it before a PDF is there (W.4.132). A tool
 * that runs on an open PDF has nothing to run on yet, so its reply asks for
 * the file first, whatever the model wrote. A tool that takes its own files
 * (Merge, Word to PDF) opens as it is: its reply stays unless it promises
 * work, and then it is the plain open line. With no tool, a question about the PDF
 * asked on the home is sent again with the file, to Okra Agent; any other
 * reply that promises work becomes the plain no-match line. The tool and its
 * button stay.
 */
function beforeFile(
  result: IntentResult,
  input: RouteIntentInput,
  source: 'home' | 'agent_panel',
  agentLater: boolean,
): IntentResult {
  const ask = source === 'home' ? 'Attach a PDF first' : 'Open a PDF first';
  const main = input.tools.find((tool) => tool.id === result.toolId);
  if (!main) {
    const forAgent =
      source === 'home' &&
      agentLater &&
      (result.parts ? result.parts.some((part) => part.agent) : asksWhatItSays(input.prompt));
    if (forAgent) {
      return {
        ...result,
        reply: `${ask}, then send this with it. Okra Agent answers from the PDF.`,
      };
    }
    return PROMISE.test(result.reply) ? { ...result, reply: NO_MATCH_REPLY } : result;
  }
  const partial = result.parts?.some((part) => part.toolId !== main.id) ?? false;
  const scope = partial ? ' for part of this' : '';
  if (!main.requiresDocument) {
    return PROMISE.test(result.reply) ? { ...result, reply: `Open ${main.title}${scope}.` } : result;
  }
  return { ...result, reply: `${ask}, then open ${main.title}${scope}.` };
}

// ── Agent ────────────────────────────────────────────────────────────────────

// Mirrors of the endpoint's gates: outside them the server answers 400.
const MAX_PROMPT_CHARS = 1000;
const MAX_TOOLS = 64;
const TOOL_ID_PATTERN = /^[a-z0-9-]{1,64}$/;
const MAX_TOOL_TITLE_CHARS = 60;
const MAX_TOOL_DESCRIPTION_CHARS = 160;
/** Ids, titles and descriptions of the whole catalogue, added up. */
const MAX_CATALOGUE_CHARS = 8000;
const MAX_DOCUMENT_NAME_CHARS = 120;
const MAX_REWRITE_CHARS = 2000;
const MIN_REWRITE_MAX_CHARS = 20;
const MAX_REWRITE_MAX_CHARS = 3000;

/** The panel shows its own notice; this is what a caller sees if it surfaces the error. */
const REWRITE_FAILED_MESSAGE = 'Okra Agent could not rewrite this text.';

type PromptFailureReason =
  | 'login_required'
  | 'rate_limited'
  | 'ai_unavailable'
  | 'ai_failed'
  | 'invalid_request'
  | 'network';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The endpoint's `error` token, when it is one this client knows. */
function serverReason(code: unknown): PromptFailureReason | null {
  switch (code) {
    case 'login_required':
    case 'rate_limited':
    case 'ai_unavailable':
    case 'ai_failed':
    case 'invalid_request':
      return code;
    default:
      return null;
  }
}

function statusReason(status: unknown): PromptFailureReason {
  switch (status) {
    case 'FETCH_ERROR':
    case 'TIMEOUT_ERROR':
      return 'network';
    case 400:
      return 'invalid_request';
    case 401:
      return 'login_required';
    case 429:
      return 'rate_limited';
    case 503:
      return 'ai_unavailable';
    default:
      return 'ai_failed';
  }
}

function failureReason(error: unknown): PromptFailureReason {
  if (!isRecord(error)) return 'ai_failed';
  const { status, data } = error;
  // The base query's login_required fallback rewrites `error` to human copy
  // and keeps the token in `code`, so read `code` first.
  const fromBody = isRecord(data) ? (serverReason(data.code) ?? serverReason(data.error)) : null;
  return fromBody ?? statusReason(status);
}

/**
 * The Agent's `parts`: `undefined` when there are none to show (an answer from
 * before they existed, or one request), `null` when they are not the contract.
 * A part's id the host does not have becomes "no tool", as the server does.
 */
function readAgentParts(
  value: unknown,
  known: ReadonlySet<string>,
): IntentPart[] | null | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > MAX_PARTS) return null;
  const parts: IntentPart[] = [];
  for (const entry of value) {
    if (!isRecord(entry) || typeof entry.request !== 'string' || !entry.request.trim()) return null;
    if (entry.toolId !== null && typeof entry.toolId !== 'string') return null;
    if (entry.agent !== undefined && typeof entry.agent !== 'boolean') return null;
    const toolId =
      typeof entry.toolId === 'string' && known.has(entry.toolId) ? entry.toolId : null;
    parts.push({
      request: entry.request.trim().slice(0, MAX_PART_CHARS),
      toolId,
      agent: entry.toolId === null && entry.agent === true,
    });
  }
  return parts.length > 1 ? parts : undefined;
}

/**
 * The Agent's answer, or `null` when the body is not the route contract. Ids
 * are checked against the tools that were sent: the reply drives navigation,
 * so an id the host does not have must never come back out.
 */
function readAgentResult(data: unknown, known: ReadonlySet<string>): IntentResult | null {
  if (!isRecord(data) || data.kind !== 'route') return null;
  if (typeof data.reply !== 'string' || !data.reply.trim()) return null;
  const parts = readAgentParts(data.parts, known);
  // Parts that are not the contract make the whole answer suspect: the local matcher answers.
  if (parts === null) return null;
  const toolId = typeof data.toolId === 'string' && known.has(data.toolId) ? data.toolId : null;
  const alternatives = new Set<string>();
  if (Array.isArray(data.alternatives)) {
    for (const id of data.alternatives) {
      if (typeof id === 'string' && known.has(id) && id !== toolId) alternatives.add(id);
    }
  }
  return {
    toolId,
    reply: data.reply.trim(),
    alternatives: [...alternatives].slice(0, MAX_ALTERNATIVES),
    engine: 'agent',
    ...(parts ? { parts } : {}),
  };
}

/** Analytics must never break routing: a capture that throws is dropped. */
function quietly(track: () => void): void {
  try {
    track();
  } catch {
    /* analytics unavailable — skip */
  }
}

type StudioRouteTrigger = ReturnType<typeof useStudioRouteMutation>[0];
type StudioRewriteTrigger = ReturnType<typeof useStudioRewriteMutation>[0];

export type WireTool = { id: string; title: string; description: string };

/**
 * The catalogue as the endpoint accepts it. A tool with an id it would reject
 * is left out; a long title or description is cut, not dropped; and tools come
 * off the end until the whole list is inside the size budget. The workspace
 * Agent's chat takes the same shape, so its client shapes the list here too.
 */
export function catalogueFor(tools: IntentTool[]): WireTool[] {
  const catalogue = tools
    .filter((tool) => TOOL_ID_PATTERN.test(tool.id) && tool.title.trim() !== '')
    .slice(0, MAX_TOOLS)
    .map(({ id, title, description }) => ({
      id,
      title: title.slice(0, MAX_TOOL_TITLE_CHARS),
      description: description.slice(0, MAX_TOOL_DESCRIPTION_CHARS),
    }));
  let size = 0;
  const overflow = catalogue.findIndex((tool) => {
    size += tool.id.length + tool.title.length + tool.description.length;
    return size > MAX_CATALOGUE_CHARS;
  });
  return overflow === -1 ? catalogue : catalogue.slice(0, overflow);
}

async function askAgent(
  send: StudioRouteTrigger,
  input: RouteIntentInput,
  prompt: string,
): Promise<IntentResult | PromptFailureReason> {
  try {
    // Signed out: skip a request that can only answer 401 and would open
    // sign-up over a prompt the local matcher can answer without an account.
    if (!(await getClerkToken())) return 'login_required';
    const tools = catalogueFor(input.tools);
    if (tools.length === 0) return 'invalid_request';
    const known = new Set(tools.map((tool) => tool.id));
    const data: unknown = await send({
      prompt,
      tools,
      document: input.document
        ? {
            name: input.document.name.slice(0, MAX_DOCUMENT_NAME_CHARS),
            pageCount: Math.max(0, Math.trunc(input.document.pageCount) || 0),
          }
        : null,
      // The open tool may be one the catalogue leaves out (the Agent panel itself).
      activeToolId:
        input.activeToolId !== null && known.has(input.activeToolId) ? input.activeToolId : null,
    }).unwrap();
    return readAgentResult(data, known) ?? 'ai_failed';
  } catch (error) {
    return failureReason(error);
  }
}

/**
 * Route a prompt to a tool. `route` never throws: the Agent answers when it
 * can, the local matcher otherwise. `hasFile` labels the analytics event, and
 * with no `document` either it means the visitor has no PDF yet: the reply
 * then asks for one before anything else (`beforeFile`).
 */
export function useRouteIntent(source: 'home' | 'agent_panel'): {
  route: (input: RouteIntentInput, options?: { hasFile?: boolean }) => Promise<IntentResult>;
  pending: boolean;
} {
  const [studioRoute] = useStudioRouteMutation();
  // A count, not a flag: the home composer and a retry can overlap.
  const [inFlight, setInFlight] = useState(0);

  const route = useCallback(
    async (input: RouteIntentInput, options?: { hasFile?: boolean }): Promise<IntentResult> => {
      quietly(() => trackStudioPromptSubmitted({ source, has_file: options?.hasFile ?? false }));

      const prompt = input.prompt.trim().slice(0, MAX_PROMPT_CHARS);
      let result: IntentResult | null = null;
      // Without the Agent there is no request to make, and nothing failed.
      if (prompt && studioAgentEnabled()) {
        setInFlight((count) => count + 1);
        const outcome = await askAgent(studioRoute, input, prompt);
        setInFlight((count) => count - 1);
        if (typeof outcome === 'string') {
          quietly(() => trackStudioPromptFailed({ source, reason: outcome }));
        } else {
          result = outcome;
        }
      }
      result ??= matchIntentLocally(input.prompt, input.tools);
      if (result.engine === 'agent' && !result.parts) {
        const uncovered = uncoveredParts(result, input.prompt, input.tools);
        if (uncovered.length > 0) result = { ...result, parts: uncovered };
      }
      const noFile = !options?.hasFile && input.document === null;
      // The Agent reads a PDF it is given; with one already here, routing means it could not.
      const agentLater = noFile && studioAgentEnabled();
      if (result.parts) {
        result = {
          ...result,
          parts: result.parts.map((part) => ({ ...part, agent: part.agent && agentLater })),
        };
      }
      if (noFile) result = beforeFile(result, input, source, agentLater);

      const routed = result;
      quietly(() =>
        trackStudioPromptRouted({
          source,
          engine: routed.engine,
          tool: routed.toolId ?? 'none',
          matched: routed.toolId !== null,
        }),
      );
      return routed;
    },
    [source, studioRoute],
  );

  return { route, pending: inFlight > 0 };
}

async function askRewrite(
  send: StudioRewriteTrigger,
  input: Parameters<StudioRewriteText>[0],
): Promise<string | null> {
  if (!input.text.trim() || input.text.length > MAX_REWRITE_CHARS) return null;
  try {
    const data: unknown = await send({
      text: input.text,
      instruction: input.instruction,
      maxChars: Math.min(
        MAX_REWRITE_MAX_CHARS,
        Math.max(MIN_REWRITE_MAX_CHARS, Math.ceil(input.maxChars) || 0),
      ),
    }).unwrap();
    if (!isRecord(data) || typeof data.text !== 'string') return null;
    return data.text.trim() || null;
  } catch {
    return null;
  }
}

/**
 * The Studio's `rewriteText` seam. Resolves with the rewritten block, or
 * rejects with a fixed message — the server's reason is never shown.
 */
export function useRewriteText(): StudioRewriteText {
  const [studioRewrite] = useStudioRewriteMutation();

  return useCallback<StudioRewriteText>(
    async (input) => {
      const text = await askRewrite(studioRewrite, input);
      quietly(() =>
        trackStudioRewriteRun({
          instruction: input.instruction,
          outcome: text === null ? 'failed' : 'ok',
        }),
      );
      if (text === null) throw new Error(REWRITE_FAILED_MESSAGE);
      return text;
    },
    [studioRewrite],
  );
}
